import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { enhancedProducts } from '@/data/enhancedProducts';
import { Product } from '@/types/product';
import SearchAndFilter, { FilterState } from './SearchAndFilter';
import ProductGrid from './ProductGrid';
import ProductQuickView from './ProductQuickView';

const SearchableProductPage: React.FC = () => {
  const [filteredProducts, setFilteredProducts] = useState<Product[]>(enhancedProducts);
  const [currentFilters, setCurrentFilters] = useState<FilterState | null>(null);
  const [quickViewProduct, setQuickViewProduct] = useState<Product | null>(null);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);

  const handleFilterChange = useCallback((products: Product[], filters: FilterState) => {
    setFilteredProducts(products);
    setCurrentFilters(filters);
  }, []);

  const handleQuickView = useCallback((product: Product) => {
    setQuickViewProduct(product);
    setIsQuickViewOpen(true);
  }, []);

  const handleCloseQuickView = useCallback(() => {
    setIsQuickViewOpen(false);
    setQuickViewProduct(null);
  }, []);

  const handleAddToCart = useCallback((product: Product, variant: any, quantity: number = 1) => {
    console.log('Adding to cart:', { product: product.name, variant, quantity });
    // TODO: Implement actual cart functionality
    // For now, just show success message or integrate with existing cart system
  }, []);

  const getCategoryStats = () => {
    const totalProducts = filteredProducts.length;
    const totalAvailable = enhancedProducts.length;
    const categories = [...new Set(filteredProducts.map(p => p.category))];
    const featuredCount = filteredProducts.filter(p => p.featured).length;
    
    return { totalProducts, totalAvailable, categories, featuredCount };
  };

  const stats = getCategoryStats();

  return (
    <section className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-lime-400/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-lime-400/3 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-16">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8 md:mb-12"
        >
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-black bg-gradient-to-r from-lime-400 via-lime-300 to-lime-400 bg-clip-text text-transparent mb-4">
            All Products
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto">
            Discover our complete collection of premium pickleball apparel and accessories. 
            Use the filters below to find exactly what you need.
          </p>
          
          {/* Stats */}
          <div className="flex flex-wrap justify-center items-center gap-4 md:gap-6 mt-6 text-white/80">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-lime-400 rounded-full"></div>
              <span className="text-sm md:text-base">
                {stats.totalProducts} of {stats.totalAvailable} products
              </span>
            </div>
            <div className="w-1 h-1 bg-gray-500 rounded-full hidden sm:block"></div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-lime-400 rounded-full"></div>
              <span className="text-sm md:text-base">
                {stats.categories.length} categories
              </span>
            </div>
            <div className="w-1 h-1 bg-gray-500 rounded-full hidden sm:block"></div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-lime-400 rounded-full"></div>
              <span className="text-sm md:text-base">
                {stats.featuredCount} featured items
              </span>
            </div>
          </div>
        </motion.div>

        {/* Search and Filter Component */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-8 md:mb-12"
        >
          <SearchAndFilter
            products={enhancedProducts}
            onFilterChange={handleFilterChange}
          />
        </motion.div>

        {/* Active Filters Display */}
        {currentFilters && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="mb-6"
          >
            <div className="flex flex-wrap gap-2">
              {currentFilters.searchQuery && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-lime-400/20 text-lime-300 border border-lime-400/30">
                  Search: "{currentFilters.searchQuery}"
                </span>
              )}
              {currentFilters.category !== 'all' && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-400/20 text-blue-300 border border-blue-400/30">
                  Category: {currentFilters.category.replace('s', "'s")}
                </span>
              )}
              {currentFilters.subcategory !== 'all' && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-400/20 text-purple-300 border border-purple-400/30">
                  Type: {currentFilters.subcategory}
                </span>
              )}
              {currentFilters.badge !== 'all' && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-400/20 text-orange-300 border border-orange-400/30">
                  Badge: {currentFilters.badge}
                </span>
              )}
              {currentFilters.inStock && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-400/20 text-green-300 border border-green-400/30">
                  In Stock Only
                </span>
              )}
            </div>
          </motion.div>
        )}

        {/* Product Grid */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <ProductGrid
            products={filteredProducts}
            onQuickView={handleQuickView}
            onAddToCart={handleAddToCart}
            className="mb-16"
          />
        </motion.div>

        {/* Load More / Pagination Placeholder */}
        {filteredProducts.length > 12 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <button className="bg-lime-400 hover:bg-lime-500 text-black font-bold py-4 px-8 rounded-xl text-lg transition-all duration-300 hover:shadow-xl hover:scale-105">
              Load More Products
            </button>
          </motion.div>
        )}
      </div>

      {/* Quick View Modal */}
      <ProductQuickView
        product={quickViewProduct}
        isOpen={isQuickViewOpen}
        onClose={handleCloseQuickView}
        onAddToCart={handleAddToCart}
      />
    </section>
  );
};

export default SearchableProductPage; 